import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import { ChangeEvent, useState } from 'react';
import { IoLogIn } from 'react-icons/io5';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { USERNAME_REGEX } from '../../constants/settings';
import useCheckActions from '../../hooks/APIHooks/users/useCheckActions';
import useClientActions from '../../hooks/APIHooks/users/useClientActions';
import { signupFormSchema } from '../../schemas';
import { fetchErrorMessage } from '../../utils/fetchErrorMessage';
import InputFieldBadge from '../CommonComps/Badges/InputFieldBadge';
import AcademiaButton from '../CommonComps/Buttons/AcademiaButton';
import AcademiaCheckboxField from '../CommonComps/Inputs/AcademiaCheckboxField';
import AcademiaInputField from '../CommonComps/Inputs/AcademiaInputField';
import BadgeLoading from '../CommonComps/Loadings/BadgeLoading';
import PasswordRestrictionRules from '../CommonComps/PasswordRestrictionRules';
import SocialLogins from './SocialLogins';

const SignupForm = () => {
  // integration of react hooks here
  const [username, setUsername] = useState<string>('');

  // integration of react-router-dom hooks here
  const navigate = useNavigate();

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // integration of custom API hooks here
  const { createNewClientHandler } = useClientActions('');

  const { getCheckAndSuggestUsername } = useCheckActions(username);

  // handler function to handle sign up form submission declared here
  const signupFormSubmissionHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      console.log(values);

      const body = new FormData();

      body.append('username', values.username.toLowerCase());
      body.append('email', values.email);
      body.append('password', values.password);
      body.append('created_by', 'Anonymous');
      body.append('is_active', 'true');

      await createNewClientHandler
        .mutateAsync({ body })
        .then((res) => {
          if (res.is_success) {
            toast.success('New User Created!! Now Login..');

            navigate('/');
          } else {
            toast.error(res.message || 'User Creation Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      console.log(error);
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering signup form section component here
  return (
    <motion.section
      initial={{ x: 500, opacity: 0 }}
      animate={{
        x: 0,
        opacity: 1,
        transition: {
          ease: 'easeInOut',
          duration: 0.4,
        },
      }}
      className='w-[95%] md:w-2/3 2xl:w-1/2 mx-auto my-auto'
    >
      <div className='space-y-1 mb-5'>
        <h1 className='text-3xl 2xl:text-4xl italic text-legal-dark-blue font-bold'>
          Sign Up
        </h1>
        <p className='text-slate-500'>
          Sign up today and experience our premium legal consultancy, tailored
          just for you.
        </p>
      </div>

      <Formik
        initialValues={{
          username: username.toLowerCase(),
          email: '',
          password: '',
          confirm_password: '',
          is_agree: false,
        }}
        validationSchema={signupFormSchema}
        onSubmit={signupFormSubmissionHandler}
      >
        {(props) => (
          <Form className='space-y-4'>
            <div>
              <AcademiaInputField
                type='text'
                name='username'
                label='Username'
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setUsername(e.target.value);
                  props.setFieldValue('username', e.target.value);

                  if (USERNAME_REGEX.test(e.target.value)) {
                    queryClient.invalidateQueries({
                      queryKey: ['check-and-suggest-username', e.target.value],
                    });
                  }
                }}
                placeholder='Enter username here...'
                isRequired
                badge={
                  getCheckAndSuggestUsername.isLoading ? (
                    <BadgeLoading />
                  ) : getCheckAndSuggestUsername.data &&
                    props.values.username ? (
                    <InputFieldBadge
                      badgeText=''
                      badgeValue={getCheckAndSuggestUsername.data.message}
                      isValid={
                        getCheckAndSuggestUsername.data.message === 'Available'
                      }
                    />
                  ) : null
                }
              />

              <AnimatePresence
                initial={false}
                mode='wait'
                onExitComplete={() => null}
              >
                {getCheckAndSuggestUsername.data?.data ? (
                  <motion.label
                    initial={{ y: -20, opacity: 0, display: 'none' }}
                    animate={
                      getCheckAndSuggestUsername.data.message ===
                      'Already Taken'
                        ? { y: 0, opacity: 1, display: 'block' }
                        : {
                            y: -20,
                            opacity: 0,
                            transitionEnd: { display: 'none' },
                          }
                    }
                    htmlFor={`input-user`}
                    className='block mt-1 text-base-red text-sm font-light'
                  >
                    Suggested:
                    {getCheckAndSuggestUsername?.data?.data.map(
                      (suggestion, index) => (
                        <button
                          key={index}
                          type='button'
                          onClick={() => {
                            setUsername(suggestion);
                            props.setFieldValue('username', suggestion);
                          }}
                          className='font-semibold text-base-cyan  hover:opacity-70 duration-300 mx-1'
                        >
                          <span className='underline'>{suggestion}</span>
                          {index <
                          (getCheckAndSuggestUsername?.data?.data
                            ?.length as number) -
                            1
                            ? ', '
                            : ''}
                        </button>
                      )
                    )}
                    etc.
                  </motion.label>
                ) : null}
              </AnimatePresence>
            </div>

            <AcademiaInputField
              type='email'
              name='email'
              label='Email'
              placeholder='Enter email here...'
              isRequired
            />

            <div>
              <AcademiaInputField
                type='password'
                name='password'
                label='Password'
                placeholder='Enter password here...'
                isRequired
              />

              <PasswordRestrictionRules
                password={props.values.password}
                isTouched={!!props.touched.password}
                error={props.errors.password}
              />
            </div>

            <div className='!mt-1'>
              <AcademiaInputField
                type='password'
                name='confirm_password'
                label='Confirm Password'
                placeholder='Retype password to confirm here...'
                isRequired
              />
            </div>

            <AcademiaCheckboxField
              name='is_agree'
              label={
                <>
                  By creating an account, you agree to the{' '}
                  <Link to={'#'} className='text-legal-brown underline italic'>
                    Terms & conditions
                  </Link>{' '}
                  and{' '}
                  <Link to={'#'} className='text-legal-brown underline italic'>
                    Privacy Policy
                  </Link>{' '}
                  of MyLegalites
                </>
              }
              isRequired
            />

            <div className='!mt-5'>
              <AcademiaButton
                title={
                  getCheckAndSuggestUsername.data?.message === 'Already Taken'
                    ? 'Please, provide a valid username!'
                    : undefined
                }
                type='submit'
                variant='premium'
                icon={<IoLogIn className='size-6' />}
                isLoading={props.isSubmitting}
                isDisabled={
                  getCheckAndSuggestUsername.data?.message === 'Already Taken'
                }
                fullWidth
              >
                Sign Up
              </AcademiaButton>

              <p className='text-sm text-legal-dark-blue text-center mt-2'>
                Already Have An Account?{' '}
                <Link
                  to={'/'}
                  className='text-base-light-blue italic underline hover:opacity-60 active:scale-95 duration-300'
                >
                  Login To Your Account
                </Link>
              </p>
            </div>
          </Form>
        )}
      </Formik>

      <SocialLogins />
    </motion.section>
  );
};

export default SignupForm;
